<svelte:options runes={false} />
<script>
  // The streaming liveness surface (U8 / KTD6). NOT the determinate backtest progress bar — a live loop has no
  // terminal total. Two INDEPENDENT, orthogonal indicators that must compose (never replace each other):
  //   1) the lifecycle status chip (server-reported state), and
  //   2) the SSE health indicator (connecting / live / reconnecting / stale).
  // When lifecycle=Running but SSE=stale, BOTH show — a running strategy whose feed is stale must never read as
  // "Running (live data)". Faulted is rendered loudly (assertive). Plus a last-bar/bar-count liveness signal and
  // the persistent alerts feed.
  import { sseConnectionState, autotradeAlerts } from '../../lib/sseStore.js';
  import NextBarCountdown from './NextBarCountdown.svelte';

  export let status = null;       // lifecycle status (server-reported)
  export let lastBarTime = null;  // ISO string of the latest bar (liveness signal)
  export let barCount = 0;
  // The strategy's bar frequency (drives the next-bar countdown for a Running strategy). 0/null hides it.
  export let frequencySeconds = 0;
  // The selected strategy's id — alerts are scoped STRICTLY to it (an alert for any OTHER strategy, including
  // a different-mode one or a since-removed one, never renders here). This surface is only ever mounted for
  // the selected strategy, so there is no "show all" case to handle.
  export let strategyId = null;
  // The selected strategy's display name (shown in brackets next to the id in each alert).
  export let strategyName = null;

  $: isFaulted = String(status || '').toLowerCase() === 'faulted';

  // SSE health → label + class. 'connected' === live.
  function sseLabel(state) {
    switch (state) {
      case 'connected': return 'Live';
      case 'connecting': return 'Connecting…';
      case 'reconnecting': return 'Reconnecting…';
      case 'stale': return 'Stale';
      default: return 'Live';
    }
  }

  function statusClass(s) {
    const v = String(s || '').toLowerCase();
    if (v === 'running') return 'at-status-running';
    if (v === 'paused' || v === 'pausing') return 'at-status-paused';
    if (v === 'faulted') return 'at-status-faulted';
    if (v === 'stopped' || v === 'stopping' || v === 'undeploying') return 'at-status-stopped';
    return 'at-status-deployed';
  }

  function fmtTime(t) {
    if (!t) return '—';
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }

  // Filter the persistent alert feed to EXACTLY this strategy (boundary fix): an alert must carry a strategyId
  // that matches the selected strategy. This drops two leaks the old `!a.strategyId` pass-through caused —
  // (1) an ownerless alert (no strategyId) showing on every strategy regardless of mode, and (2) a paper
  // strategy's alert appearing under a live strategy (and vice-versa). A removed strategy is never the
  // selected one, so its alerts can't render here either.
  $: alerts = strategyId ? $autotradeAlerts.filter((a) => a.strategyId === strategyId) : [];

  // Append the strategy NAME in brackets next to its id wherever the alert message names the bare id (the
  // backend logs ids only — it has no display name; the SPA resolves it here for the operator).
  function withName(message) {
    if (!message || !strategyId || !strategyName) return message;
    return message.split(strategyId).join(`${strategyId} (${strategyName})`);
  }
</script>

<div class="at-status-surface">
  <div class="at-status-indicators">
    <!-- (1) lifecycle status chip — server-reported. Always shown. -->
    <span class="at-status-pill {statusClass(status)}"
      role={isFaulted ? 'alert' : undefined} aria-live={isFaulted ? 'assertive' : 'off'}>
      {status ?? '—'}
    </span>

    <!-- (2) SSE health — ORTHOGONAL to lifecycle. Shown alongside, never replacing the status chip. -->
    <span class="at-sse-pill at-sse-{$sseConnectionState}" role="status" aria-live="polite"
      title="Live-data connection: {sseLabel($sseConnectionState)}">
      <span class="at-sse-dot" aria-hidden="true"></span>
      {sseLabel($sseConnectionState)}
    </span>
  </div>

  <div class="at-liveness">
    <span class="at-liveness-item">Last bar: {fmtTime(lastBarTime)}</span>
    <span class="at-liveness-item">Bars: {barCount}</span>
    <NextBarCountdown {frequencySeconds} {status} />
  </div>

  <!-- Persistent alerts feed (not auto-dismissed). Non-atomic so appended alerts don't re-announce the whole
       list (a11y politeness; faults are assertive). -->
  {#if alerts.length > 0}
    <ul class="at-alert-feed" aria-label="Strategy alerts" aria-live="polite" aria-atomic="false">
      {#each alerts as a (a.seq)}
        <li class="at-alert" class:fault={a.code === 'strategy_faulted'}>
          <span class="at-alert-code">{a.code}</span>
          <span class="at-alert-message">{withName(a.message)}</span>
        </li>
      {/each}
    </ul>
  {/if}
</div>

